import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { clerkEnabled } from "./lib/clerkEnv";
import { isFlyAppHost } from "./lib/runWhere";
import { MovedTo } from "./components/common/MovedTo";
import { CommandPalette } from "./components/common/CommandPalette";

// Analytics is only useful when we're actually deployed to Vercel. Self-hosted
// installs don't have the `/_vercel/insights/script.js` endpoint, so loading
// the package at all causes a console SyntaxError when the SPA fallback HTML
// is served in place of the missing script. Lazy-import only when on Vercel
// — this avoids the side-effect cost on every other host.
const IS_VERCEL = typeof window !== "undefined"
	&& !isFlyAppHost(window.location.hostname)
	&& window.location.hostname !== "app.agntspace.com"
	&& /vercel\.app$|agntspace\.com$/i.test(window.location.hostname);
const MaybeAnalytics = IS_VERCEL
	? lazy(() => import("@vercel/analytics/react").then((m) => ({ default: m.Analytics })))
	: () => null;
import { ShortcutHelp } from "./components/common/ShortcutHelp";
import { UndoToastHost } from "./components/common/UndoToast";
import { Shell } from "./components/layout/Shell";
import { type AccessSession, AUTH_REQUIRED_EVENT, fetchAccessSession } from "./lib/access";
import { useAccessSessionStore } from "./stores/accessSession";
// Lazy-loaded pages: EVERY route, including the entry ones. Dashboard/landing/
// setup/onboarding/legal were eager ("avoid a first-paint flash") until
// 2026-07-13 — which baked all eight into the entry chunk, so /dashboard
// parsed landing+pricing+use-cases+setup+onboarding code it never runs
// (~860ms unused-JS, Lighthouse perf 37 < the 50 budget). Same-origin chunk
// fetches resolve in tens of ms against the local server; the null Suspense
// fallback makes the "flash" invisible in practice. Vite splits per-route
// chunks so the initial bundle stays lean and every page pays only for itself.
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const LandingPage = lazy(() => import("./pages/landing/LandingPage").then((m) => ({ default: m.LandingPage })));
const AuthPage = lazy(() => import("./pages/landing/AuthPage").then((m) => ({ default: m.AuthPage })));
const EnterPage = lazy(() => import("./pages/landing/EnterPage").then((m) => ({ default: m.EnterPage })));
const SsoRoute = lazy(() => import("./pages/landing/AuthPage").then((m) => ({ default: m.SsoRoute })));
const ClerkRouterProvider = lazy(() => import("./pages/landing/ClerkRouterProvider").then((m) => ({ default: m.ClerkRouterProvider })));
const OnboardingPage = lazy(() => import("./pages/onboarding/OnboardingPage").then((m) => ({ default: m.OnboardingPage })));
const NotFoundPage = lazy(() => import("./pages/not-found/NotFoundPage").then((m) => ({ default: m.NotFoundPage })));
const PrivacyPage = lazy(() => import("./pages/legal/PrivacyPage").then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import("./pages/legal/TermsPage").then((m) => ({ default: m.TermsPage })));
const PricingPage = lazy(() => import("./pages/pricing/PricingPage").then((m) => ({ default: m.PricingPage })));
const SetupPage = lazy(() => import("./pages/setup/SetupPage").then((m) => ({ default: m.SetupPage })));
const LoginPage = lazy(() => import("./pages/access/LoginPage").then((m) => ({ default: m.LoginPage })));
const PairPage = lazy(() => import("./pages/access/PairPage").then((m) => ({ default: m.PairPage })));
const UseCasesPage = lazy(() => import("./pages/use-cases/UseCasesPage").then((m) => ({ default: m.UseCasesPage })));
const CanvasPage = lazy(() => import("./pages/canvas/CanvasPage").then((m) => ({ default: m.CanvasPage })));
const ChatPage = lazy(() => import("./pages/chat/ChatPage").then((m) => ({ default: m.ChatPage })));
const FilesPage = lazy(() => import("./pages/files/FilesPage").then((m) => ({ default: m.FilesPage })));
const AgentPage = lazy(() => import("./pages/control-panel/agent/AgentPage").then((m) => ({ default: m.AgentPage })));
const ContractPage = lazy(() => import("./pages/control-panel/contract/ContractPage").then((m) => ({ default: m.ContractPage })));
const ProfilePage = lazy(() => import("./pages/control-panel/profile/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const RelationshipPage = lazy(() => import("./pages/control-panel/relationship/RelationshipPage").then((m) => ({ default: m.RelationshipPage })));
const TrustPage = lazy(() => import("./pages/control-panel/trust/TrustPage").then((m) => ({ default: m.TrustPage })));
const DocsPage = lazy(() => import("./pages/docs/DocsPage").then((m) => ({ default: m.DocsPage })));
const SkillsPage = lazy(() => import("./pages/skills/SkillsPage").then((m) => ({ default: m.SkillsPage })));
const SkillSyncPage = lazy(() => import("./pages/skills/SkillSyncPage").then((m) => ({ default: m.SkillSyncPage })));
const AgentSyncPage = lazy(() => import("./pages/agents/AgentSyncPage").then((m) => ({ default: m.AgentSyncPage })));
const GoalsPage = lazy(() => import("./pages/goals/GoalsPage").then((m) => ({ default: m.GoalsPage })));
const GuardianPage = lazy(() => import("./pages/guardian/GuardianPage").then((m) => ({ default: m.GuardianPage })));
const HeartbeatPage = lazy(() => import("./pages/heartbeat/HeartbeatPage").then((m) => ({ default: m.HeartbeatPage })));
const WikiPage = lazy(() => import("./pages/wiki/WikiPage").then((m) => ({ default: m.WikiPage })));
const JournalPage = lazy(() => import("./pages/journal/JournalPage").then((m) => ({ default: m.JournalPage })));
const AgentsPage = lazy(() => import("./pages/agents/AgentsPage").then((m) => ({ default: m.AgentsPage })));
const RoutinesPage = lazy(() => import("./pages/routines/RoutinesPage").then((m) => ({ default: m.RoutinesPage })));
const InboxPage = lazy(() => import("./pages/inbox/InboxPage").then((m) => ({ default: m.InboxPage })));
const ConnectionsPage = lazy(() => import("./pages/connections/ConnectionsPage").then((m) => ({ default: m.ConnectionsPage })));
// /subagents redirects to /agents?view=editor — the standalone
// OrchestratorPage component still exists in pages/orchestrator/ but is
// no longer routed. AgentEditorView is imported by AgentsPage directly.
const ReferencesPage = lazy(() => import("./pages/references/ReferencesPage").then((m) => ({ default: m.ReferencesPage })));
const CostsPage = lazy(() => import("./pages/costs/CostsPage").then((m) => ({ default: m.CostsPage })));
const ExposurePage = lazy(() => import("./pages/exposure/ExposurePage").then((m) => ({ default: m.ExposurePage })));
const FinancePage = lazy(() => import("./pages/finances/FinancePage").then((m) => ({ default: m.FinancePage })));
const InvestorPage = lazy(() => import("./pages/investor/InvestorPage"));
const InstrumentPage = lazy(
	() => import("./pages/investor/InstrumentPage"),
);
// Career subsystem — Phase 1: dashboard + facts editor.
const CareerPage = lazy(() => import("./pages/career/CareerPage"));
const CareerFactsPage = lazy(() => import("./pages/career/FactsPage"));
const CareerListingsPage = lazy(() => import("./pages/career/ListingsPage"));
const CareerListingDetailPage = lazy(() => import("./pages/career/ListingDetailPage"));
const CareerApplicationsPage = lazy(() => import("./pages/career/ApplicationsPage"));
const ExtensionPage = lazy(() => import("./pages/extension/ExtensionPage").then((m) => ({ default: m.ExtensionPage })));
const ProjectDetailPage = lazy(() => import("./pages/finances/ProjectDetailPage").then((m) => ({ default: m.ProjectDetailPage })));
const CategoryDetailPage = lazy(() => import("./pages/finances/CategoryDetailPage").then((m) => ({ default: m.CategoryDetailPage })));
const MerchantDetailPage = lazy(() => import("./pages/finances/MerchantDetailPage").then((m) => ({ default: m.MerchantDetailPage })));
const TransactionsPage = lazy(() => import("./pages/finances/TransactionsPage").then((m) => ({ default: m.TransactionsPage })));
// /finance/budgets routes through FinancePage so the Budgets tab on the
// Finance page is the single home for budget management. The standalone
// BudgetsPage wrapper still exists in BudgetsPage.tsx for callers that
// want a budget-only screen, but it's no longer routed.
// /graph is the FULL page (GraphPage) — all the panels (filters, scope, presets,
// top hubs, cleanup/merge, ontology, detail) with the d3 SVG renderer swapped out
// for the live react-force-graph-2d canvas (animated like Obsidian's graph). The
// bare react-force-graph page (GraphPageCosmos) stays at /graph-cosmos, and Sigma
// (GraphPageV2, static) at /graph-next, as alternates. See tasks/plan-graph-refactor.md.
const GraphPageCosmos = lazy(() => import("./pages/graph/GraphPageCosmos").then((m) => ({ default: m.GraphPageCosmos })));
const GraphPageFull = lazy(() => import("./pages/graph/GraphPage").then((m) => ({ default: m.GraphPage })));
const GraphPageV2 = lazy(() => import("./pages/graph/v2/GraphPageV2").then((m) => ({ default: m.GraphPageV2 })));
const OntologyPage = lazy(() => import("./pages/ontology/OntologyPage").then((m) => ({ default: m.OntologyPage })));
const MemoryPage = lazy(() => import("./pages/memory/MemoryPage").then((m) => ({ default: m.MemoryPage })));
// DreamsPage merged into MemoryPage as a tab (2026-04-12)
const ProjectsPage = lazy(() => import("./pages/projects/ProjectsPage").then((m) => ({ default: m.ProjectsPage })));
const ReflectionsPage = lazy(() => import("./pages/reflections/ReflectionsPage").then((m) => ({ default: m.ReflectionsPage })));
const PluginsPage = lazy(() => import("./pages/plugins/PluginsPage").then((m) => ({ default: m.PluginsPage })));
const LLMPage = lazy(() => import("./pages/llm/LLMPage").then((m) => ({ default: m.LLMPage })));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const TasksPage = lazy(() => import("./pages/tasks/TasksPage").then((m) => ({ default: m.TasksPage })));
const FocusPage = lazy(() => import("./pages/focus/FocusPage").then((m) => ({ default: m.FocusPage })));
const TimePage = lazy(() => import("./pages/time/TimePage").then((m) => ({ default: m.TimePage })));
const CalendarPage = lazy(() => import("./pages/calendar/CalendarPage").then((m) => ({ default: m.CalendarPage })));
// Chrome-less companion window (window.open target from /focus) — routed
// OUTSIDE the Shell like /privacy, so it renders without the sidebar.
const FocusPopout = lazy(() => import("./pages/focus/FocusPopout").then((m) => ({ default: m.FocusPopout })));
const LogsPage = lazy(() => import("./pages/logs/LogsPage").then((m) => ({ default: m.LogsPage })));
const DecisionsPage = lazy(() => import("./pages/decisions/DecisionsPage").then((m) => ({ default: m.DecisionsPage })));
const ProposalsPage = lazy(() => import("./pages/proposals/ProposalsPage").then((m) => ({ default: m.ProposalsPage })));
const EvolutionPage = lazy(() => import("./pages/evolution/EvolutionPage").then((m) => ({ default: m.EvolutionPage })));
const AdminPage = lazy(() => import("./pages/admin/AdminPage").then((m) => ({ default: m.AdminPage })));
const VaultSearchPage = lazy(() => import("./pages/vault/VaultSearchPage").then((m) => ({ default: m.VaultSearchPage })));
const WorkQueuePage = lazy(() => import("./pages/work-queue/WorkQueuePage").then((m) => ({ default: m.WorkQueuePage })));
const PipelinePage = lazy(() => import("./pages/pipeline/PipelinePage").then((m) => ({ default: m.PipelinePage })));
const VideosPage = lazy(() => import("./pages/videos/VideosPage").then((m) => ({ default: m.VideosPage })));
const MediaPage = lazy(() => import("./pages/media/MediaPage").then((m) => ({ default: m.MediaPage })));
const ResearchPage = lazy(() => import("./pages/research/ResearchPage").then((m) => ({ default: m.ResearchPage })));
const KanbanPage = lazy(() => import("./pages/kanban/KanbanPage").then((m) => ({ default: m.KanbanPage })));
const CollectionsPage = lazy(() => import("./pages/collections/CollectionsPage").then((m) => ({ default: m.CollectionsPage })));
const ContactsPage = lazy(() => import("./pages/contacts/ContactsPage").then((m) => ({ default: m.ContactsPage })));
const ImportsPage = lazy(() => import("./pages/imports/ImportsPage").then((m) => ({ default: m.ImportsPage })));

function ClerkGate({ children }: { children: ReactNode }) {
	// No publishable key: a self-hosted install never downloads the auth chunk.
	if (!clerkEnabled()) return children;
	return (
		<Suspense fallback={null}>
			<ClerkRouterProvider>{children}</ClerkRouterProvider>
		</Suspense>
	);
}

export function App() {
	const [setupMode, setSetupMode] = useState<boolean | null>(null);
	// Remote access: from another machine the server asks for a credential; on
	// this machine `required` is false and none of this renders (lib/access.ts).
	const [access, setAccess] = useState<"loading" | "ok" | "login" | "expired">("loading");
	const [accessSession, setAccessSession] = useState<AccessSession | null>(null);
	useEffect(() => {
		let alive = true;
		void fetchAccessSession().then((s) => { if (alive) { setAccessSession(s); useAccessSessionStore.getState().setSession(s); setAccess(s.required && !s.authenticated ? "login" : "ok"); } });
		const onRequired = () => setAccess((cur) => (cur === "login" ? cur : "expired"));
		window.addEventListener(AUTH_REQUIRED_EVENT, onRequired);
		return () => { alive = false; window.removeEventListener(AUTH_REQUIRED_EVENT, onRequired); };
	}, []);

	useEffect(() => {
		fetch("/api/health")
			.then((r) => r.json())
			.then((d) => setSetupMode(d.setup_mode === true))
			.catch(() => setSetupMode(false));
	}, []);

	if (typeof window !== "undefined" && isFlyAppHost(window.location.hostname)) {
		window.location.replace("https://agntspace.com/enter");
		return null;
	}

	// /pair is the way IN for a device with no credential yet (remote-node N1):
	// it renders before the sign-in gate and before setup mode.
	if (typeof window !== "undefined" && window.location.pathname === "/pair") {
		return (
			<Suspense fallback={null}>
				<PairPage />
			</Suspense>
		);
	}

	// While checking setup mode, show nothing (prevents flash)
	if (access === "loading") return null;
	if (access === "login" || access === "expired") {
		return (
			<Suspense fallback={null}>
				<LoginPage
					reason={access === "expired" ? "expired" : "required"}
					plainHttp={accessSession?.plain_http_refused === true}
					httpsUrl={accessSession?.https_url || ""}
					onLoggedIn={() => { setAccess("ok"); window.location.reload(); }}
				/>
			</Suspense>
		);
	}
	if (setupMode === null) return null;

	// Setup mode: only show the folder picker
	if (setupMode) {
		return (
			<BrowserRouter>
				<Suspense fallback={null}>
					<MaybeAnalytics />
				</Suspense>
				<Routes>
					<Route path="/setup" element={<SetupPage />} />
					<Route path="*" element={<Navigate to="/setup" replace />} />
				</Routes>
			</BrowserRouter>
		);
	}

	return (
		<BrowserRouter>
			<Suspense fallback={null}>
				<MaybeAnalytics />
			</Suspense>
			<UndoToastHost />
			<CommandPalette />
			<ShortcutHelp />
			<ClerkGate>
			<Routes>
				{/* Public pages share the landing header. */}
				<Route path="/" element={<LandingPage />} />
				<Route path="/sign-in" element={<AuthPage mode="sign-in" />} />
				<Route path="/sign-up" element={<AuthPage mode="sign-up" />} />
				<Route path="/enter" element={<EnterPage />} />
				<Route path="/sign-in/sso-callback" element={<SsoRoute />} />
				<Route path="/sign-up/sso-callback" element={<SsoRoute />} />
				<Route path="/sign-in/*" element={<AuthPage mode="sign-in" />} />
				<Route path="/sign-up/*" element={<AuthPage mode="sign-up" />} />

				{/* Standalone pages: no sidebar, accessible on agntspace.com */}
				<Route path="/privacy" element={<PrivacyPage />} />
				<Route path="/terms" element={<TermsPage />} />
				<Route path="/pricing" element={<PricingPage />} />
				<Route path="/use-cases" element={<UseCasesPage />} />
				<Route path="/docs" element={<DocsPage />} />
				<Route path="/focus-popout" element={<FocusPopout />} />

				{/* App: sidebar layout */}
				<Route element={<Shell />}>
					<Route path="/dashboard" element={<DashboardPage />} />
					<Route path="/chat" element={<ChatPage />} />
					<Route path="/chat/:conversationId" element={<ChatPage />} />
					<Route path="/canvas" element={<CanvasPage />} />
					<Route path="/files" element={<FilesPage />} />
					<Route path="/journal" element={<JournalPage />} />
					<Route path="/tasks" element={<TasksPage />} />
					<Route path="/focus" element={<FocusPage />} />
					<Route path="/time" element={<TimePage />} />
					<Route path="/calendar" element={<CalendarPage />} />
					<Route path="/kanban" element={<KanbanPage />} />
					<Route path="/kanban/:slug" element={<KanbanPage />} />
					<Route path="/collections" element={<CollectionsPage />} />
					<Route path="/collections/:slug" element={<CollectionsPage />} />
					<Route path="/contacts" element={<ContactsPage />} />
					<Route path="/imports" element={<ImportsPage />} />
					<Route path="/goals" element={<GoalsPage />} />
					<Route path="/projects" element={<ProjectsPage />} />
					<Route path="/reflections" element={<ReflectionsPage />} />
					<Route path="/search" element={<VaultSearchPage />} />
					<Route path="/logs" element={<LogsPage />} />
					<Route path="/decisions" element={<DecisionsPage />} />
					<Route path="/proposals" element={<ProposalsPage />} />
					<Route path="/evolution" element={<EvolutionPage />} />
					<Route path="/admin" element={<AdminPage />} />
					<Route path="/guardian" element={<GuardianPage />} />
					<Route path="/heartbeat" element={<HeartbeatPage />} />
					<Route path="/wiki" element={<WikiPage />} />
					<Route path="/wiki/:slug" element={<WikiPage />} />
					<Route path="/graph" element={<GraphPageFull />} />
					<Route path="/graph-cosmos" element={<GraphPageCosmos />} />
					<Route path="/graph-next" element={<GraphPageV2 />} />
					<Route path="/graph-legacy" element={<GraphPageFull />} />
					<Route path="/ontology" element={<OntologyPage />} />
					<Route path="/memory" element={<MemoryPage />} />
					<Route path="/dreams" element={<MovedTo to="/memory" add={{ layer: "dreams" }} />} />
					<Route path="/references" element={<ReferencesPage />} />
					<Route path="/costs" element={<CostsPage />} />
					<Route path="/exposure" element={<ExposurePage />} />
					<Route path="/finance" element={<FinancePage />} />
					<Route path="/investor" element={<InvestorPage />} />
					<Route
						path="/investor/instrument/:instrumentId"
						element={<InstrumentPage />}
					/>
					<Route path="/finance/transactions" element={<TransactionsPage />} />
					<Route path="/finance/ledger" element={<FinancePage />} />
					<Route path="/finance/budgets" element={<FinancePage />} />
					<Route path="/finance/reports" element={<FinancePage />} />
					<Route path="/finance/learning" element={<FinancePage />} />
					<Route path="/finance/merchants" element={<FinancePage />} />
					<Route path="/finance/categories" element={<FinancePage />} />
					<Route path="/finance/duplicates" element={<FinancePage />} />
					<Route path="/finance/projects/:slug" element={<ProjectDetailPage />} />
					<Route path="/finance/categories/:slug" element={<CategoryDetailPage />} />
					<Route path="/finance/merchants/:slug" element={<MerchantDetailPage />} />
					{/* Career subsystem — Phase 1: dashboard + facts editor. */}
					<Route path="/career" element={<CareerPage />} />
					<Route path="/career/facts" element={<CareerFactsPage />} />
					{/* Pre-2026-08-16 URL for the per-item facts page — facts are facts. */}
					<Route path="/career/items" element={<MovedTo to="/career/facts" />} />
					<Route path="/career/listings" element={<CareerListingsPage />} />
					<Route path="/career/listings/:slug" element={<CareerListingDetailPage />} />
					<Route path="/career/applications" element={<CareerApplicationsPage />} />
					<Route path="/career/applications/:slug" element={<CareerApplicationsPage />} />
					<Route path="/extension" element={<ExtensionPage />} />
					<Route path="/control/profile" element={<ProfilePage />} />
					<Route path="/control/contract" element={<ContractPage />} />
					<Route path="/control/agent" element={<AgentPage />} />
					<Route path="/control/relationship" element={<RelationshipPage />} />
					<Route path="/control/trust" element={<TrustPage />} />
					<Route path="/plugins" element={<PluginsPage />} />
					<Route path="/work-queue" element={<WorkQueuePage />} />
					<Route path="/pipeline" element={<PipelinePage />} />
					<Route path="/research" element={<ResearchPage />} />
					<Route path="/videos" element={<VideosPage />} />
					<Route path="/media" element={<MediaPage />} />
					<Route path="/llm" element={<LLMPage />} />
					<Route path="/settings" element={<SettingsPage />} />
					<Route path="/skills" element={<SkillsPage />} />
					<Route path="/skills/sync" element={<SkillSyncPage />} />
					<Route path="/agents/sync" element={<AgentSyncPage />} />
					<Route path="/agents" element={<AgentsPage />} />
					{/* /bots is the roster's LEGACY address (A0, 2026-09-02). The product
					 *  noun is "agent" (owner decision 2026-09-03) and the sidebar points at
					 *  /agents; this alias only keeps old links resolving. */}
					<Route path="/bots" element={<AgentsPage />} />
				{/* Routines (A3, 2026-09-02): recurring tasks an agent carries out. */}
				<Route path="/routines" element={<RoutinesPage />} />
				{/* Inbox (A4, 2026-09-03): approvals + proposals waiting on the user. */}
				<Route path="/inbox" element={<InboxPage />} />
				<Route path="/connections" element={<ConnectionsPage />} />
					{/* Legacy /subagents route — Editor now lives as a tab on
					 *  /agents. Redirect to preserve any old bookmarks /
					 *  internal links. MovedTo carries the link's own query
					 *  across, so `/subagents?agent=<key>` still opens that
					 *  agent (it used to be dropped, opening the first agent).
					 *  New links use lib/routes `agentRoute(key)`. */}
					<Route path="/subagents" element={<MovedTo to="/agents" add={{ view: "editor" }} />} />
					<Route path="/onboarding" element={<OnboardingPage />} />
					{/* An unknown address says so (2026-10-02). It used to redirect to
					 *  /dashboard with replace, so a dead bookmark or a server deep_link
					 *  naming a missing route looked like it had worked. */}
					<Route path="*" element={<NotFoundPage />} />
				</Route>
			</Routes>
			</ClerkGate>
		</BrowserRouter>
	);
}
